import React from 'react';
import { t, Trans } from '@lingui/macro';

import Cat from '@rover/icons/svg/cat.svg';
import Fence from '@rover/icons/svg/fence.svg';
import NoCat from '@rover/icons/svg/no-cats.svg';
import NoDog from '@rover/icons/svg/no-dogs.svg';
import NotFixed from '@rover/icons/svg/not-fixed.svg';
import Pets from '@rover/icons/svg/pets.svg';
import Puppy from '@rover/icons/svg/puppy.svg';
import { Flex, Heading, Text } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import getSearchUrl from '@rover/react-lib/src/utils/getSearchUrl';
import { PetType } from '@rover/types/src/Pet';
import type { SearchFilters } from '@rover/types/src/SearchFilters';

import { ValuePropFilterOptionType } from '../../utilities/analyticsEvents';
import { PageSectionId, useSectionRef } from '../hooks/useSectionRef';

import SquareIconButton from './SquareIconButton';

type ValuePropFilterOptionsProps = {
  locationName: string;
  language: string;
  searchFilters: Partial<SearchFilters>;
  acceptsPuppies: boolean;
  onPress?: (valuePropFilterOption: ValuePropFilterOptionType) => void;
} & Pick<React.ComponentProps<typeof Section>, 'my' | 'mt' | 'mb'>;

type SquareIconButtonProps = React.ComponentProps<typeof SquareIconButton>;

const ValuePropTile: React.FC<SquareIconButtonProps & { href?: string }> = ({ href, ...props }) => {
  return <SquareIconButton {...props} href={href} flex="1 1 48%" />;
};

const ValuePropFilterOptions: React.FC<ValuePropFilterOptionsProps> = ({
  locationName,
  language,
  searchFilters,
  acceptsPuppies,
  onPress = () => {},
  ...marginProps
}) => {
  const sectionRef = useSectionRef(PageSectionId.ValuePropFilterOptions, { threshold: 1.0 });

  return (
    <Section
      htmlRef={sectionRef}
      contentWidth="full"
      paddingLeft="4x"
      paddingRight="4x"
      maxWidth={['720px', '940px', '1140px']}
      {...marginProps}
    >
      <Heading size="400" mb="3x">
        <Trans>Popular requests near you</Trans>
      </Heading>
      <Text>
        <Trans>Select a filter and find qualifying {locationName} sitters!</Trans>
      </Text>
      <Flex
        mt="6x"
        alignContent="stretch"
        flexDirection={['column', 'row']}
        flexWrap="wrap"
        gap="4x"
      >
        <ValuePropTile
          icon={NoCat}
          title={t`Doesn't own a cat`}
          href={getSearchUrl(
            { ...searchFilters, noCats: true, bypassInterstitial: true },
            language
          )}
          onPress={() => {
            onPress(ValuePropFilterOptionType.NO_CAT);
          }}
        />
        <ValuePropTile
          icon={NoDog}
          title={t`Doesn't own a dog`}
          href={getSearchUrl(
            { ...searchFilters, personDoesNotHaveDogs: true, bypassInterstitial: true },
            language
          )}
          onPress={() => {
            onPress(ValuePropFilterOptionType.NO_DOG);
          }}
        />
        <ValuePropTile
          icon={Fence}
          title={t`Has a fenced yard`}
          href={getSearchUrl(
            { ...searchFilters, hasFencedYard: true, bypassInterstitial: true },
            language
          )}
          onPress={() => {
            onPress(ValuePropFilterOptionType.HAS_FENCED_YARD);
          }}
        />
        <ValuePropTile
          icon={NotFixed}
          title={t`Accepts non-spayed or neutered pets`}
          href={getSearchUrl(
            {
              ...searchFilters,
              unspayedFemales: true,
              nonNeuteredMales: true,
              bypassInterstitial: true,
            },
            language
          )}
          onPress={() => {
            onPress(ValuePropFilterOptionType.NOT_FIXED);
          }}
        />
        <ValuePropTile
          icon={Pets}
          title={t`Accepts only one pet at a time`}
          href={getSearchUrl(
            { ...searchFilters, acceptsOnlyOneClient: true, bypassInterstitial: true },
            language
          )}
          onPress={() => {
            onPress(ValuePropFilterOptionType.ONLY_ONE_PET);
          }}
        />
        {acceptsPuppies ? (
          <ValuePropTile
            icon={Puppy}
            title={t`Accepts puppies`}
            href={getSearchUrl(
              { ...searchFilters, puppy: true, bypassInterstitial: true },
              language
            )}
            onPress={() => {
              onPress(ValuePropFilterOptionType.ACCEPTS_PUPPIES);
            }}
          />
        ) : (
          <ValuePropTile
            icon={Cat}
            title={t`Accepts cats`}
            href={getSearchUrl(
              { ...searchFilters, petType: [PetType.CAT], catCare: true, bypassInterstitial: true },
              language
            )}
            onPress={() => {
              onPress(ValuePropFilterOptionType.ACCEPTS_CATS);
            }}
          />
        )}
      </Flex>
    </Section>
  );
};

export default ValuePropFilterOptions;
